// This file does not have dependencies for easy of copy-pasting into IFrame widgets

/**
 * Multi-item, multi-stage progress state
 * E.g. track progress of 10 files with each a "download", "convert", "load" stage
 */
export type Progress<S extends string> = {
  id: string
  byStage: Record<S, ProgressByItem>
}

export type ProgressByItem = Record<string, ProgressItemStage>

export type ProgressStatus = ProgressStatusInput["status"]

export type ProgressItemStage = {
  status: "idle"
} | {
  status: "loading"
  progress?: number
} | {
  status: "error"
  progress?: number
  error: Error
} | {
  status: "done"
  progress: 1
}

export type ProgressInput<S extends string>
  = { type: "reset" }
  | ({ type: "update" } & ProgressUpdate<S>)

export type ProgressUpdate<S extends string> = {
  item: string
  stage: S
} & ProgressStatusInput

export type ProgressStatusInput = (
  | { status: "idle" | "done" }
  | { status: "loading", progress?: number }
  | { status: "error", error: Error }
)

export function scopedProgress<S extends string>(args: {
  item: string
  stage: S
  progressCallback?: (input: ProgressUpdate<S>) => void
}) {
  return (input: ProgressStatusInput) => {
    args.progressCallback?.({
      item: args.item,
      stage: args.stage,
      ...input
    })
  }
}

export async function runStageWithProgress<S extends string, T>(args: {
  item: string
  stage: S
  progressCallback?: (input: ProgressUpdate<S>) => void
  run: (progress: (p?: number) => void) => Promise<T>
}): Promise<T> {
  const progress = scopedProgress({
    item: args.item,
    stage: args.stage,
    progressCallback: args.progressCallback
  })

  try {
    progress({ status: "loading", progress: 0 })

    const result = await args.run((p?: number) => {
      progress({ status: "loading", progress: p })
    })

    progress({ status: "done" })
    return result
  } catch (e) {
    const error = e instanceof Error ? e : new Error(String(e))
    progress({ status: "error", error })
    throw e
  }
}
